import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabaseClient';
import { useCurrentUserProfile } from '../contexts/CurrentUserProfileContext';
import { useToast } from '../contexts/ToastContext';
import { shareLink } from '../lib/shareLink';
import { buildAppUrl } from '../lib/siteUrl';
import { NOTIFICATION_TYPES } from '../utils/notifications';
import { isEventActionClosed } from '../utils/eventCutoff';
import {
  useGroupMembershipWrite,
  isGroupMutationQueuedError,
} from './mutations/useGroupMembershipWrite';
import { invalidateAfterGroupMembershipChange } from '../lib/query/invalidateMutations';
import { queryKeys } from '../lib/query/queryKeys';
import { readCachedGroupDisplay } from '../lib/query/groupDisplayCache';
import {
  GROUP_ROW_SELECT,
  GROUP_MEMBERS_SELECT,
  isDbUuid,
  formatGroupMemberDate,
  mapMembersRows,
  buildEventForCard,
} from '../lib/groupInfoQueries';
import { SURFACES, GROUPS_INDEX_PATH } from '../config/productSurfaces';
import { stashPendingAction, readPendingAction, clearPendingAction } from '../lib/pendingAction';
import {
  buildGroupInviteShare,
  resetGroupInviteCode,
  checkGroupInvite,
  joinGroupRpc,
} from '../lib/groupInviteLink';
import {
  onNotificationRow,
  MEMBERSHIP_NOTIFICATION_TYPES,
} from '../lib/realtime/notificationEvents';
import {
  readGroupPage,
  writeGroupPage,
  dropGroupPage,
  fetchGroupPage,
  fetchGoingCount,
} from '../lib/groupPageCache';
import { usePromptAuth } from '../components/auth/AuthPrompt';

/** While a manager has the page open, pending requests are re-read this often even if no notification arrives. */
const JOIN_REQUESTS_POLL_MS = 20_000;

function seedFromCache(queryClient, userId, groupId) {
  return readCachedGroupDisplay(queryClient, userId, groupId);
}

const readSnap = readGroupPage;
const writeSnap = writeGroupPage;

export default function useGroupInfoPage(groupId) {
  const promptAuth = usePromptAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { session, profile, authReady } = useCurrentUserProfile();
  const { showToast } = useToast();
  const userId = session?.user?.id;
  const groupMembershipWrite = useGroupMembershipWrite(userId);

  const cachedDisplay = useMemo(
    () => seedFromCache(queryClient, userId, groupId),
    [queryClient, userId, groupId],
  );

  const [group, setGroup] = useState(null);
  const [event, setEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [offline, setOffline] = useState(false); // the load could not reach the server and nothing was saved to show
  const [blockedUsers, setBlockedUsers] = useState(new Set());
  const [shared, setShared] = useState(false);
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]);
  const [eventRsvpGoingCount, setEventRsvpGoingCount] = useState(0);
  const [attendeeCount, setAttendeeCount] = useState(0);
  // Joining from a shared link (see joinGroup below).
  const [inviteValid, setInviteValid] = useState(false);
  const [joining, setJoining] = useState(false);
  const [joinRequestSent, setJoinRequestSent] = useState(false);

  const [managerAccessType, setManagerAccessType] = useState('private');
  const [managerGroupName, setManagerGroupName] = useState(() => cachedDisplay?.name ?? '');
  const [isEditingManagerGroupName, setIsEditingManagerGroupName] = useState(false);
  const [managerGroupNameDraft, setManagerGroupNameDraft] = useState('');
  const [savingManagerGroupName, setSavingManagerGroupName] = useState(false);
  const [managerGroupRenameError, setManagerGroupRenameError] = useState('');
  const [savingManagerAccessType, setSavingManagerAccessType] = useState(false);

  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(null);
  const [postBlockReportUserId, setPostBlockReportUserId] = useState(null);
  const [showRemoveModal, setShowRemoveModal] = useState(null);
  const [memberPhotoZoom, setMemberPhotoZoom] = useState(null);
  const [openActionsMemberId, setOpenActionsMemberId] = useState(null);

  const managerAccessSavedTimerRef = useRef(null);
  const openActionsRowRef = useRef(null);
  const fetchRunRef = useRef(0);
  // Someone joined, left or was approved while this page is open: load it again (the saved copy stays on screen meanwhile).
  const [reloadTick, setReloadTick] = useState(0);
  const loadedGroupRef = useRef(null);
  useEffect(
    () =>
      onNotificationRow((row) => {
        if (
          row?.related_group_id === groupId &&
          row.type !== 'join_request' &&
          MEMBERSHIP_NOTIFICATION_TYPES.has(row.type)
        )
          setReloadTick((n) => n + 1);
      }),
    [groupId],
  );

  const currentMember = members.find((m) => m.user_id === userId && !m.left_at);
  const isLegacyLeadMember = Boolean(
    currentMember && !currentMember.role && members.length > 0 && members[0]?.user_id === userId,
  );
  const isMember = members.some((m) => m.user_id === userId && !m.left_at);
  const isManager =
    currentMember?.role === 'creator' || currentMember?.role === 'manager' || isLegacyLeadMember;
  const activeMemberCount = members.filter((m) => !m.left_at).length;
  const canLeaveGroup = isMember;
  const isPublicGroup = (group?.access_type || 'private') === 'public';
  const isArchived = group?.status === 'archived' || group?.archived_at;
  const isPrivateGroupSetting = managerAccessType === 'private';
  const groupDisplayName = managerGroupName || group?.name || cachedDisplay?.name || null;
  const headerTitlePending = loading && !groupDisplayName;
  const visibleMemberCount = members.filter((m) => !blockedUsers.has(m.user_id)).length;
  const eventForCard = useMemo(
    () => buildEventForCard(event, eventRsvpGoingCount),
    [event, eventRsvpGoingCount],
  );
  // Where "back" goes from the group's info page. With chat hidden the info page IS the
  // group's home, and /groups/:id redirects straight back here — so back has to be the
  // groups index, or Back does nothing at all.
  const chatPath = SURFACES.chats && groupId ? `/groups/${groupId}` : GROUPS_INDEX_PATH;

  useEffect(
    () => () => {
      if (managerAccessSavedTimerRef.current) {
        clearTimeout(managerAccessSavedTimerRef.current);
      }
    },
    [],
  );

  const refreshMembers = useCallback(async () => {
    if (!groupId) return;
    const { data } = await supabase
      .from('group_members')
      .select(GROUP_MEMBERS_SELECT)
      .eq('group_id', groupId)
      .is('left_at', null)
      .order('joined_at', { ascending: true });
    if (data) {
      setMembers(mapMembersRows(data));
      setAttendeeCount(data.length);
    }
  }, [groupId]);

  const fetchPendingJoinRequests = useCallback(async () => {
    if (!isManager || !groupId) {
      setPendingJoinRequests([]);
      return;
    }
    if (event && isEventActionClosed(event)) {
      await supabase
        .from('group_join_requests')
        .delete()
        .eq('group_id', groupId)
        .eq('status', 'pending');
      setPendingJoinRequests([]);
      return;
    }
    const { data, error: reqError } = await supabase
      .from('group_join_requests')
      .select(
        `
        *,
        profiles:user_id(id, display_name, profile_photo_url)
      `,
      )
      .eq('group_id', groupId)
      .eq('status', 'pending')
      .order('created_at', { ascending: true });
    if (reqError) {
      console.error('[GroupInfo] fetchPendingJoinRequests error:', reqError);
      setPendingJoinRequests([]);
      return;
    }
    setPendingJoinRequests(data || []);
  }, [groupId, isManager, event]);

  useEffect(() => {
    if (!groupId || !authReady) return undefined; // signed-in or not is known first: no signed-out fetch, then a second
    const runId = ++fetchRunRef.current;
    const displayCache = readCachedGroupDisplay(queryClient, userId, groupId);
    // Seen this session: show it at once and refresh quietly behind it (Sam, 2026-10-20: going away and back showed the
    // loader every time - nothing here was cached).
    const snapKey = groupId; // by group: the session arriving is not a different page
    const snap = readSnap(queryClient, userId, snapKey);
    setError('');
    setOffline(false);
    if (snap) {
      setGroup(snap.group);
      setEvent(snap.event);
      setMembers(snap.members);
      setBlockedUsers(snap.blocked);
      setAttendeeCount(snap.attendeeCount);
      setEventRsvpGoingCount(snap.rsvpGoing);
      setManagerAccessType(snap.group.access_type || 'private');
      setManagerGroupName(snap.group.name || '');
      setManagerGroupNameDraft(snap.group.name || '');
      setLoading(false);
    } else setLoading(true);
    if (snap) {
      /* the rest of the reset below is for a group not seen yet */
    } else if (displayCache?.name) {
      setManagerGroupName(displayCache.name);
      setManagerGroupNameDraft(displayCache.name);
    } else {
      setManagerGroupName('');
      setManagerGroupNameDraft('');
      setGroup(null);
      setMembers([]);
    }
    if (!snap) {
      setEvent(null);
      setBlockedUsers(new Set());
    }
    if (loadedGroupRef.current !== groupId) setPendingJoinRequests([]); // a reload of the same group keeps them on screen
    loadedGroupRef.current = groupId;

    (async () => {
      const res = await fetchGroupPage(groupId, userId, snap);
      if (runId !== fetchRunRef.current) return;
      setLoading(false);
      // Offline (or the server unreachable): the saved page stays as it is; with none, say so quietly.
      if (res.status === 'offline') {
        if (!snap) setOffline(true);
        return;
      }
      if (res.status === 'notFound') {
        dropGroupPage(queryClient, userId, snapKey);
        setError('Group not found or you do not have access.');
        return;
      }
      const {
        group: groupData,
        event: eventRow,
        members: mapped,
        blocked,
        attendeeCount: count,
      } = res.snap;
      setGroup(groupData);
      setEvent(eventRow);
      setAttendeeCount(count);
      if (res.membersFetched) setMembers(mapped);
      setBlockedUsers(blocked);
      setManagerAccessType(groupData.access_type || 'private');
      setManagerGroupName(groupData.name || '');
      setManagerGroupNameDraft(groupData.name || '');
      writeSnap(queryClient, userId, snapKey, res.snap);
      if (groupData.event_id) {
        fetchGoingCount(groupData.event_id)
          .then((going) => {
            if (runId !== fetchRunRef.current) return;
            setEventRsvpGoingCount(going);
            const cur = readSnap(queryClient, userId, snapKey);
            if (cur) writeSnap(queryClient, userId, snapKey, { ...cur, rsvpGoing: going });
          })
          .catch(() => {});
      } else setEventRsvpGoingCount(0);
    })();

    return () => {
      fetchRunRef.current += 1;
    };
  }, [groupId, userId, authReady, reloadTick]);
  // Edits made here (a rename, a member removed or promoted, a join) go into the snapshot too, so coming back never shows
  // the page as it was before them.
  useEffect(() => {
    if (loading || !group || !groupId) return;
    const key = groupId;
    const cur = readSnap(queryClient, userId, key);
    writeSnap(queryClient, userId, key, {
      group,
      event,
      members,
      blocked: blockedUsers,
      attendeeCount,
      rsvpGoing: cur?.rsvpGoing ?? eventRsvpGoingCount,
    });
  }, [
    loading,
    group,
    event,
    members,
    blockedUsers,
    attendeeCount,
    eventRsvpGoingCount,
    groupId,
    userId,
  ]);

  useEffect(() => {
    fetchPendingJoinRequests();
  }, [fetchPendingJoinRequests]);

  // A request to join used to sit unseen until the page was reopened (2026-10-03). join_group writes a
  // `join_request` notification for the managers, which the Header's realtime channel hands out; a slow poll
  // covers a channel that dropped on the festival network.
  useEffect(() => {
    if (!isManager || !groupId) return undefined;
    const stop = onNotificationRow((row) => {
      if (row?.type === NOTIFICATION_TYPES.JOIN_REQUEST && row.related_group_id === groupId)
        fetchPendingJoinRequests();
    });
    if (event && isEventActionClosed(event)) return () => stop();
    const timer = setInterval(() => {
      if (typeof document === 'undefined' || !document.hidden) fetchPendingJoinRequests();
    }, JOIN_REQUESTS_POLL_MS);
    return () => {
      stop();
      clearInterval(timer);
    };
  }, [isManager, groupId, event, fetchPendingJoinRequests]);

  const openMemberProfile = (targetUserId) => {
    if (!targetUserId) return;
    navigate(targetUserId === userId ? '/profile' : `/profile/${targetUserId}`);
  };

  // The shared link carries an invite code, so a friend it reaches joins in one tap even though every group
  // starts private (create_private_group). Without a code - someone who found the group another way - joining
  // a private group is a request the managers approve.
  const inviteCode = useMemo(
    () => new URLSearchParams(location.search).get('invite')?.trim() || null,
    [location.search],
  );

  // Expired: the event is over (join_group's own rule) or the group is archived. An expired group is read-only - no
  // renaming, inviting, access or member changes (Sam, 2026-10-20); leaving stays allowed. The server holds the same line
  // (migration 157).
  const isJoinClosed = Boolean(event) && isEventActionClosed(event);
  const isExpired = Boolean(isArchived) || isJoinClosed;
  const expiredToast = () => showToast('This group has ended.');

  const copyShareLink = async () => {
    if (isExpired) {
      expiredToast();
      return;
    }
    const { path, text } = await buildGroupInviteShare(supabase, {
      groupId,
      isMember: Boolean(userId && isMember),
      event,
    });
    const result = await shareLink({
      url: buildAppUrl(path),
      title: group?.name || 'Group on Prii',
      text,
    });
    if (result.method === 'clipboard') {
      setShared(true);
      setTimeout(() => setShared(false), 2000);
    } else if (result.method === 'failed') {
      showToast('Failed to share link.');
    }
  };

  useEffect(() => {
    if (!userId || !groupId || !inviteCode) {
      setInviteValid(false);
      return undefined;
    }
    let cancelled = false;
    checkGroupInvite(supabase, groupId, inviteCode).then((ok) => {
      if (!cancelled) setInviteValid(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [userId, groupId, inviteCode]);

  const canJoinDirectly = isPublicGroup || inviteValid;

  // One way in (migration 128): join_group decides join vs request on the server - public group, a live link on a
  // group that does not require approval, never for someone a manager removed - marks you going, and tells the
  // managers. The client only reports the outcome.
  const joinGroup = async () => {
    if (!userId || !groupId || !group || joining || isMember) return;
    if (isJoinClosed) {
      showToast('Joining is closed for this event.');
      return;
    }
    setJoining(true);
    try {
      const outcome = await joinGroupRpc(supabase, groupId, inviteCode);
      if (outcome === 'joined' || outcome === 'already_member') {
        await refreshMembers();
        invalidateAfterGroupMembershipChange(queryClient, {
          userId,
          groupId,
          eventId: group.event_id,
        });
        queryClient.invalidateQueries({ queryKey: ['find'] });
        queryClient.invalidateQueries({ queryKey: ['groups'] });
        // join_group marked you going too: the RSVP list and the Maps index must not lag (2026-10-04 audit).
        queryClient.invalidateQueries({ queryKey: ['rsvps', 'mine', userId] });
        // The Maps index only: ['festival-map'] also matched every map payload and rebuilt the open 3D scene.
        queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.mine(userId) });
        queryClient.invalidateQueries({ queryKey: queryKeys.festivalMap.upcoming() });
        showToast("You're in! Your group can find you on the map now.");
      } else if (outcome === 'requested' || outcome === 'already_requested') {
        setJoinRequestSent(true);
        showToast('Request sent - a group manager will let you in.');
      } else if (outcome === 'full') {
        showToast('This group is full.');
      } else if (outcome === 'closed') {
        showToast('Joining is closed for this event.');
      } else {
        showToast('Could not join the group.');
      }
    } catch (err) {
      console.error('[GroupInfo] joinGroup error:', err);
      showToast('Could not join the group.');
    } finally {
      setJoining(false);
    }
  };

  // Manager controls for the link (WhatsApp's "Reset link" and "Approve new members").
  const [approveNewMembers, setApproveNewMembers] = useState(false);
  useEffect(() => {
    if (!groupId || !isManager) return undefined;
    let cancelled = false;
    // Read on its own, and failure-tolerant: the column arrives with migration 128.
    supabase
      .from('groups')
      .select('approve_new_members')
      .eq('id', groupId)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled) setApproveNewMembers(Boolean(data?.approve_new_members));
      });
    return () => {
      cancelled = true;
    };
  }, [groupId, isManager]);
  const setApproveNewMembersSetting = async (next) => {
    if (!groupId || !isManager) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    const prev = approveNewMembers;
    setApproveNewMembers(next);
    const { error: updErr } = await supabase
      .from('groups')
      .update({ approve_new_members: next })
      .eq('id', groupId);
    if (updErr) {
      setApproveNewMembers(prev);
      showToast('Could not change that setting.');
    }
  };
  const resetInviteLink = async () => {
    if (!groupId || !isManager) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    const code = await resetGroupInviteCode(supabase, groupId);
    showToast(
      code ? 'Invite link reset. The old link no longer works.' : 'Could not reset the link.',
    );
  };

  /** Signed out: remember the join and sign in in the pop-up over this page (Stage 8); signed in, the invite joins by
   *  itself below (its link), and the × drops the join. */
  const signInToJoin = (title) => {
    stashPendingAction({ type: 'join_group_chat', groupId, inviteCode });
    promptAuth({ action: 'Join', target: title || 'this group' });
  };

  // The join a signed-out visitor asked for, replayed once they are back and the group has loaded. Waits for the
  // invite check so a private group with a good link joins rather than requests.
  const [inviteChecked, setInviteChecked] = useState(false);
  useEffect(() => {
    setInviteChecked(false);
  }, [inviteCode, userId]);
  useEffect(() => {
    if (inviteValid || !inviteCode) setInviteChecked(true);
  }, [inviteValid, inviteCode]);
  useEffect(() => {
    if (!inviteCode || !userId || inviteChecked) return undefined;
    const t = window.setTimeout(() => setInviteChecked(true), 1500);
    return () => window.clearTimeout(t);
  }, [inviteCode, userId, inviteChecked]);
  // An invite link joins by itself (Sam, 2026-09-28: share it to a group chat, everyone taps it, they are in - no
  // Join button). Once per link per app session, so Back or a reload after leaving the group does not re-join.
  // A group that requires approval turns this into a request, the same as tapping Join would.
  useEffect(() => {
    if (!userId || !groupId || loading || !group || !inviteChecked) return;
    const pending = readPendingAction();
    const pendingHere =
      pending?.type === 'join_group_chat' && String(pending.groupId) === String(groupId);
    if (pendingHere) clearPendingAction();
    let viaLink = false;
    if (inviteCode && inviteValid) {
      const key = `prii:inviteAutoJoin:${groupId}:${inviteCode}`;
      try {
        viaLink = !sessionStorage.getItem(key);
        sessionStorage.setItem(key, '1');
      } catch {
        viaLink = true;
      }
    }
    if (!pendingHere && !viaLink) return;
    if (isMember || isJoinClosed) return;
    void joinGroup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, groupId, loading, group, inviteChecked, inviteValid, isMember]);

  const approveJoinRequest = async (request) => {
    if (!groupId || !request?.id || !request?.user_id) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    try {
      // One transaction on the server (migration 154): membership, request, going RSVP, system message, join_approved
      // and member_joined. Five client writes with unchecked errors could leave a member whose request still read
      // pending and who was never told (2026-10-16 audit).
      const { data: result, error } = await supabase.rpc('approve_join_request', {
        p_request_id: request.id,
      });
      if (error) throw error;
      const outcome = result?.outcome;
      if (outcome !== 'approved' && outcome !== 'already_resolved') {
        showToast(outcome === 'closed' ? 'This group is closed.' : 'Failed to approve request.');
        fetchPendingJoinRequests();
        return;
      }
      // The approver's own Find and member list pick the new member up now: no notification reaches this phone
      // for its own approval, and Find otherwise waited for its 45 s re-read (2026-10-14 Turnstile).
      invalidateAfterGroupMembershipChange(queryClient, {
        userId: userId,
        groupId,
        eventId: group?.event_id || null,
      });

      setPendingJoinRequests((prev) => prev.filter((item) => item.id !== request.id));

      const memberId = result?.member_id;
      if (outcome === 'approved' && memberId && isDbUuid(memberId)) {
        setMembers((prev) => {
          const alreadyActive = prev.some((m) => m.user_id === request.user_id && !m.left_at);
          if (alreadyActive) return prev;
          return [
            ...prev,
            {
              id: memberId,
              group_id: groupId,
              user_id: request.user_id,
              role: 'member',
              join_type: 'late_join',
              joined_at: result?.joined_at || new Date().toISOString(),
              left_at: null,
              profile: request?.profiles || null,
            },
          ];
        });
        setAttendeeCount((prev) => prev + 1);
      }

      const joinedName = request?.profiles?.display_name || 'Someone';
      fetchPendingJoinRequests();
      showToast(`Request approved — ${joinedName} has been added to the group.`);
    } catch (err) {
      console.error('[GroupInfo] approveJoinRequest error:', err);
      showToast('Failed to approve request.');
    }
  };

  const denyJoinRequest = async (request) => {
    if (!groupId || !request?.id || !request?.user_id) return;
    try {
      await supabase
        .from('group_join_requests')
        .update({
          status: 'denied',
          resolved_at: new Date().toISOString(),
          resolved_by: userId || null,
        })
        .eq('id', request.id);
      setPendingJoinRequests((prev) => prev.filter((item) => item.id !== request.id));
      await supabase.from('notifications').insert({
        user_id: request.user_id,
        type: NOTIFICATION_TYPES.JOIN_DENIED,
        title: 'Your join request was not approved.',
        message: 'Your join request was not approved.',
        related_group_id: groupId,
        related_event_id: group?.event_id || null,
        related_user_id: userId || null,
        read: false,
      });
      fetchPendingJoinRequests();
      showToast('Request denied.');
    } catch (err) {
      console.error('[GroupInfo] denyJoinRequest error:', err);
      showToast('Failed to deny request.');
    }
  };

  const startEditingGroupName = () => {
    if (isExpired) return;
    const base = managerGroupName || group?.name || '';
    setManagerGroupRenameError('');
    setManagerGroupNameDraft(base);
    setIsEditingManagerGroupName(true);
  };

  const cancelEditingGroupName = () => {
    const base = managerGroupName || group?.name || '';
    setManagerGroupRenameError('');
    setManagerGroupNameDraft(base);
    setIsEditingManagerGroupName(false);
  };

  // nameOverride: the naming card saves its own field in one step. Button onClicks pass an event, hence the typeof.
  const saveManagerGroupName = async (nameOverride) => {
    if (!groupId || !isManager || savingManagerGroupName) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    const trimmedName = (
      typeof nameOverride === 'string' ? nameOverride : managerGroupNameDraft || ''
    ).trim();
    if (!trimmedName) {
      setManagerGroupRenameError('Group name cannot be empty.');
      return;
    }
    if (trimmedName.length > 40) {
      setManagerGroupRenameError('Group name must be 40 characters or fewer.');
      return;
    }
    if (trimmedName === (managerGroupName || group?.name || '').trim()) {
      setManagerGroupRenameError('');
      setIsEditingManagerGroupName(false);
      return;
    }

    setSavingManagerGroupName(true);
    setManagerGroupRenameError('');
    try {
      const { error: rpcError } = await supabase.rpc('rename_group', {
        p_group_id: groupId,
        p_new_name: trimmedName,
      });
      if (rpcError) {
        setManagerGroupRenameError(
          typeof rpcError.message === 'string' && rpcError.message.trim()
            ? rpcError.message
            : 'Failed to save group name.',
        );
        return;
      }
      const { data: refreshed } = await supabase
        .from('groups')
        .select(GROUP_ROW_SELECT)
        .eq('id', groupId)
        .maybeSingle();
      if (refreshed) setGroup((prev) => ({ ...(prev || {}), ...refreshed }));
      else setGroup((prev) => (prev ? { ...prev, name: trimmedName } : prev));
      setManagerGroupName(trimmedName);
      setManagerGroupNameDraft(trimmedName);
      setIsEditingManagerGroupName(false);
      // Every other screen holding this group's name (Groups tab, Find's card and picker, the chat caches) refetches;
      // before, the new name only appeared after a restart (operator, 2026-09-23).
      queryClient.invalidateQueries({ queryKey: ['find'] });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      invalidateAfterGroupMembershipChange(queryClient, { userId, groupId });
      showToast('Group name saved.');
    } catch (err) {
      console.error('[GroupInfo] saveManagerGroupName error:', err);
      setManagerGroupRenameError(err?.message || 'Failed to save group name.');
    } finally {
      setSavingManagerGroupName(false);
    }
  };

  const updateManagerAccessType = async (isPrivate) => {
    if (!groupId || !isManager || savingManagerAccessType) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    const nextAccessType = isPrivate ? 'private' : 'public';
    const previousAccessType = managerAccessType;
    if (nextAccessType === previousAccessType) return;

    setManagerAccessType(nextAccessType);
    setSavingManagerAccessType(true);
    try {
      const { data, error: updateError } = await supabase
        .from('groups')
        .update({ access_type: nextAccessType })
        .eq('id', groupId)
        .select(GROUP_ROW_SELECT)
        .single();
      if (updateError) throw updateError;
      setGroup(data || ((prev) => ({ ...prev, access_type: nextAccessType })));
      // The session snapshot too, or coming back shows the old setting and then flips (2026-10-21).
      const snapNow = readSnap(queryClient, userId, groupId);
      if (snapNow)
        writeSnap(queryClient, userId, groupId, {
          ...snapNow,
          group: { ...snapNow.group, ...(data || {}), access_type: nextAccessType },
        });
    } catch (err) {
      console.error('[GroupInfo] updateManagerAccessType error:', err);
      setManagerAccessType(previousAccessType);
      showToast('Failed to update access.');
    } finally {
      setSavingManagerAccessType(false);
    }
  };

  const promoteMemberToManager = async (member) => {
    if (!isManager || !member?.id || !member?.user_id || !isDbUuid(member.id)) return;
    if (isExpired) {
      expiredToast();
      return;
    }
    const currentRole = member.role || 'member';
    if (currentRole === 'creator' || currentRole === 'manager') return;

    const { data: targetRow, error: targetError } = await supabase
      .from('group_members')
      .select('id, role')
      .eq('id', member.id)
      .maybeSingle();
    if (targetError || !targetRow) {
      showToast('Unable to promote member.');
      return;
    }
    if (targetRow.role === 'creator') {
      showToast('Creator role cannot be changed.');
      return;
    }

    const { data: updatedRow, error } = await supabase
      .from('group_members')
      .update({ role: 'manager' })
      .eq('id', member.id)
      .select('id')
      .maybeSingle();
    if (error || !updatedRow) {
      console.error('[GroupInfo] promoteMemberToManager error:', error);
      showToast('Failed to promote member.');
      return;
    }

    await supabase.from('notifications').insert({
      user_id: member.user_id,
      type: NOTIFICATION_TYPES.PROMOTED_TO_MANAGER,
      title: 'You were promoted to manager',
      message: `You are now a manager in ${group?.name || 'this group'}.`,
      related_group_id: groupId,
      related_event_id: group?.event_id || null,
      related_user_id: userId || null,
      read: false,
    });
    showToast('Member promoted to manager.');
    await refreshMembers();
  };

  const removeMemberFromGroup = async (member) => {
    if (!isManager || !groupId || !member?.id || !member?.user_id || !isDbUuid(member.id)) return;
    if (member.role === 'creator' || member.user_id === userId) return;
    if (isExpired) {
      expiredToast();
      return;
    }

    const removedAt = new Date().toISOString();
    const { error: removeError } = await supabase
      .from('group_members')
      .update({ left_at: removedAt, removed: true })
      .eq('id', member.id)
      .is('left_at', null);
    if (removeError) {
      console.error('[GroupInfo] removeMemberFromGroup error:', removeError);
      showToast('Failed to remove member.');
      return;
    }
    setMembers((prev) => prev.filter((m) => m.id !== member.id));
    setAttendeeCount((prev) => Math.max(0, prev - 1));
    // This phone's Find drops them now; theirs and everyone else's hear it over group_members realtime (154).
    invalidateAfterGroupMembershipChange(queryClient, {
      userId: userId,
      groupId,
      eventId: group?.event_id || null,
    });
    showToast('Member removed from group.');
    await fetchPendingJoinRequests();
  };

  const handleConfirmRemoveMember = async () => {
    if (!showRemoveModal) return;
    await removeMemberFromGroup(showRemoveModal);
    setShowRemoveModal(null);
  };

  const openLeaveGroupModal = () => {
    setOpenActionsMemberId(null);
    setShowLeaveModal(true);
  };

  const handleLeaveGroup = async () => {
    if (!groupId || !userId) return;
    const membershipRowId = currentMember?.id;
    const displayName = profile?.display_name || 'Someone';

    groupMembershipWrite.mutate(
      {
        action: 'leave',
        groupId,
        userId,
        membershipRowId,
        displayName,
        groupName: group?.name,
        postSystemMessage: true,
      },
      {
        onSuccess: async () => {
          if (group?.event_id && isDbUuid(group.event_id)) {
            await supabase
              .from('group_matching_queue')
              .delete()
              .eq('user_id', userId)
              .eq('event_id', group.event_id);
          }
          invalidateAfterGroupMembershipChange(queryClient, { userId, groupId });
          showToast("You've left the group.");
          navigate(GROUPS_INDEX_PATH);
        },
        onError: (err) => {
          if (isGroupMutationQueuedError(err)) {
            showToast(err.message);
            return;
          }
          console.error('[GroupInfo] Error leaving group:', err);
          showToast('Failed to leave group.');
        },
      },
    );
  };

  const handleReportUserSubmit = async ({ reason, details }) => {
    if (!showReportModal || !userId) return;
    const { error: reportError } = await supabase.from('reports').insert({
      reporter_user_id: userId,
      reported_user_id: showReportModal,
      group_id: groupId,
      reason,
      details: details || null,
    });
    if (reportError) {
      console.error('[GroupInfo] Error submitting report:', reportError);
      showToast('Failed to submit report. Please try again.');
      return;
    }
    setShowReportModal(null);
    showToast('Report submitted. Thank you.');
  };

  const handleBlockUser = async () => {
    if (!showBlockModal || !userId) return;
    const { error: blockError } = await supabase.from('user_blocks').insert({
      blocker_id: userId,
      blocked_user_id: showBlockModal,
    });
    if (blockError) {
      console.error('[GroupInfo] Error blocking user:', blockError);
      showToast('Failed to block user. Please try again.');
      return;
    }
    const blockedId = showBlockModal;
    setBlockedUsers((prev) => new Set([...prev, blockedId]));
    setShowBlockModal(null);
    setOpenActionsMemberId(null);
    showToast('User blocked. Their messages are now hidden.');
    setPostBlockReportUserId(blockedId);
  };

  const handleUnblockUser = async (targetId) => {
    if (!targetId || !userId) return;
    const { error: unblockError } = await supabase
      .from('user_blocks')
      .delete()
      .eq('blocker_id', userId)
      .eq('blocked_user_id', targetId);
    if (unblockError) {
      console.error('[GroupInfo] Error unblocking user:', unblockError);
      showToast('Failed to unblock user. Please try again.');
      return;
    }
    setBlockedUsers((prev) => {
      const next = new Set(prev);
      next.delete(targetId);
      return next;
    });
    setOpenActionsMemberId(null);
    showToast('User unblocked.');
  };

  return {
    session,
    userId,
    groupId,
    group,
    event,
    members,
    loading,
    error,
    offline,
    blockedUsers,
    shared,
    pendingJoinRequests,
    inviteCode,
    inviteValid,
    canJoinDirectly,
    isJoinClosed,
    joining,
    joinRequestSent,
    joinGroup,
    signInToJoin,
    approveNewMembers,
    setApproveNewMembersSetting,
    resetInviteLink,
    eventForCard,
    groupDisplayName,
    headerTitlePending,
    cachedDisplay,
    visibleMemberCount,
    attendeeCount,
    isMember,
    isManager,
    isArchived,
    isExpired,
    isPublicGroup,
    isPrivateGroupSetting,
    canLeaveGroup,
    activeMemberCount,
    chatPath,
    managerGroupName,
    isEditingManagerGroupName,
    managerGroupNameDraft,
    setManagerGroupNameDraft,
    savingManagerGroupName,
    managerGroupRenameError,
    setManagerGroupRenameError,
    savingManagerAccessType,
    showLeaveModal,
    setShowLeaveModal,
    showReportModal,
    setShowReportModal,
    showBlockModal,
    setShowBlockModal,
    postBlockReportUserId,
    setPostBlockReportUserId,
    showRemoveModal,
    setShowRemoveModal,
    memberPhotoZoom,
    setMemberPhotoZoom,
    openActionsMemberId,
    setOpenActionsMemberId,
    openActionsRowRef,
    isDbUuid,
    formatDate: formatGroupMemberDate,
    openMemberProfile,
    copyShareLink,
    approveJoinRequest,
    denyJoinRequest,
    startEditingGroupName,
    cancelEditingGroupName,
    saveManagerGroupName,
    updateManagerAccessType,
    promoteMemberToManager,
    openLeaveGroupModal,
    handleLeaveGroup,
    handleReportUserSubmit,
    handleBlockUser,
    handleUnblockUser,
    handleConfirmRemoveMember,
  };
}
